import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FaClone, FaSearch, FaTimesCircle } from "react-icons/fa";
import PromptWriter from "./PromptWriter";
import { useToast } from "../common/ToastContext";
import yaml from "js-yaml";

const TestConfigTab = ({
  currentTestConfigs,
  onTestConfigAdd,
  onTestConfigSave,
}) => {
  const [testConfigs, setTestConfigs] = useState(currentTestConfigs || []);
  const [selectedTestConfig, setSelectedTestConfig] = useState(null);
  const [selectedTestConfigContent, setSelectedTestConfigContent] =
    useState("");
  const [updatedTestConfigContent, setUpdatedTestConfigContent] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [launchConfigName, setLaunchConfigName] = useState("");
  const [newContent, setNewContent] = useState(null);
  const { showToast } = useToast();

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const sortTestConfigs = useCallback((testConfigList) => {
    if (!Array.isArray(testConfigList)) return [];
    return [...testConfigList].sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const filteredTestConfigs = useMemo(() => {
    if (!Array.isArray(testConfigs)) {
      return [];
    }

    let filtered = testConfigs;
    if (searchTerm) {
      filtered = testConfigs.filter(
        (testConfig) =>
          testConfig.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          testConfig.persona.toLowerCase().includes(searchTerm.toLowerCase()) ||
          testConfig.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (testConfig.modality
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) &&
            testConfig.name !== "validation")
      );
    }

    return sortTestConfigs(filtered);
  }, [searchTerm, testConfigs, sortTestConfigs]);

  const openTestConfigInformation = (testConfig) => {
    setSelectedTestConfig(testConfig);
    const content = renderConfigContent(testConfig);
    setSelectedTestConfigContent(content);
    setUpdatedTestConfigContent(content);
    setShowModal(true);
  };

  const closeModal = useCallback(() => {
    setShowModal(false);
    setSelectedTestConfig(null);
  }, [setShowModal, setSelectedTestConfig]);

  const renderTestConfigContent = (content) => {
    const testConfigContent =
      "# Assistant Configuration\n" +
      yaml.dump({
        modality: content?.modality,
        persona: content?.persona,
        subject: content?.subject,
        variables: content?.variables,
      });
    return testConfigContent;
  };

  const renderValidationConfigContent = (content) => {
    const validationConfigContent =
      "# Validation Configuration\n" +
      "# Parts of this file are used for on-demand & champions validation and external presentation\n\n" +
      "# Assistant configuration\n" +
      yaml.dump({
        persona: content?.persona,
        subject: content?.subject,
        variables: content?.variables,
      });
    return validationConfigContent;
  };

  const renderConfigContent = (config) => {
    if (config.name === "validation") {
      return renderValidationConfigContent(config);
    } else {
      return renderTestConfigContent(config);
    }
  };

  const handleSaveConfig = async () => {
    const configId = selectedTestConfig.id;
    const name = selectedTestConfig.name;
    if (updatedTestConfigContent.trim() === selectedTestConfigContent.trim()) {
      showToast("No changes to save", "info");
      return;
    }
    const newConfigs = await onTestConfigSave(
      configId,
      name,
      updatedTestConfigContent
    );
    if (newConfigs === testConfigs) {
      return;
    }
    setTestConfigs(newConfigs);
    showToast("Test config saved successfully", "success");
  };

  const handleConfigChange = (newContent) => {
    if (!newContent) newContent = " "; // HACK, keeps editor from hiding
    setUpdatedTestConfigContent(newContent);
  };

  const handleAddConfig = async (name, type) => {
    const configs = await onTestConfigAdd(name, type, newContent);
    setShowNameModal(false);
    setTestConfigs(configs);
    setNewContent(null);
    const newConfig = configs.find((config) => config.name === name);
    if (newConfig) {
      openTestConfigInformation(newConfig);
    }
  };

  const displaySubtitle = (testConfig) => {
    let subtitle = `${testConfig.persona}`;
    if (testConfig.name !== "validation") {
      subtitle += ` | ${testConfig.modality}`;
    }
    return subtitle;
  };

  return (
    <div className="h-full flex flex-col">
      <div className="relative p-2 border-b border-gray-700 flex justify-between items-center">
        <FaSearch className="absolute left-5 text-gray-400" />
        <input
          type="text"
          placeholder="Filter test configs ..."
          className="w-full bg-gray-800 p-2 pl-8 rounded text-white mr-4"
          value={searchTerm}
          onChange={handleSearch}
          aria-label="Filter test configs ..."
        />
        <button
          onClick={() => setShowNameModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 px-4 rounded whitespace-nowrap"
        >
          Add New
        </button>
        {testConfigs.filter((tc) => tc.name === "validation").length === 0 && (
          <button
            onClick={() => handleAddConfig("validation", "validation")}
            className="bg-green-600 hover:bg-green-700 text-white text-sm py-2 px-4 rounded whitespace-nowrap ml-2"
          >
            Add Validation Config
          </button>
        )}
      </div>

      <div className="flex-grow overflow-y-auto dark-slim-scrollbar">
        {!filteredTestConfigs || filteredTestConfigs.length === 0 ? (
          <div className="p-4 text-gray-400">
            No test configs match your search criteria.
          </div>
        ) : (
          <div>
            {filteredTestConfigs.map((testConfig) => {
              return (
                <div
                  key={testConfig.name}
                  className="flex justify-between items-center p-4 border-b border-gray-700 hover:bg-gray-700 cursor-pointer"
                  onClick={() => openTestConfigInformation(testConfig)}
                >
                  <div>
                    <div className="text-white font-medium">
                      {testConfig.name || "Unnamed Test Config"}
                    </div>
                    <div className="text-gray-400 text-sm">
                      {displaySubtitle(testConfig)}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onTestConfigAdd(testConfig);
                    }}
                    className="text-gray-400 hover:text-white"
                    title="Add Test Config"
                  >
                    <FaClone className="text-xl" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 h-full overflow-y-auto">
          <div className="bg-gray-800 text-white rounded-lg shadow-lg w-4/5 max-w-4xl max-h-screen">
            <div className="flex justify-between items-center p-4 border-b border-gray-700">
              <h2 className="text-xl font-semibold">
                {selectedTestConfig.name || "Unnamed Test Config"}{" "}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-white"
              >
                <FaTimesCircle className="text-xl" />
              </button>
            </div>

            <div className="p-4">
              <div className="mb-4 width-full">
                <div className="bg-gray-700 p-2 rounded text-white w-full h-[400px]">
                  <PromptWriter
                    value={selectedTestConfigContent}
                    onChange={handleConfigChange}
                    language={"yaml"}
                    readOnly={false}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-between p-4 border-t border-gray-700">
              <div>
                <button
                  onClick={closeModal}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded"
                >
                  Close
                </button>
              </div>
              <div className="space-x-2">
                {selectedTestConfig.name !== "validation" && (
                  <button
                    onClick={() => {
                      setShowNameModal(true);
                      setNewContent(updatedTestConfigContent);
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded"
                  >
                    Create New
                  </button>
                )}
                <button
                  onClick={handleSaveConfig}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showNameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-96">
            <h2 className="text-xl font-semibold text-white mb-4">
              Enter Test Config Name
            </h2>
            <input
              type="text"
              className="w-full p-2 mb-4 bg-gray-700 text-white rounded"
              placeholder="Test Config Name"
              onChange={(e) => setLaunchConfigName(e.target.value)}
            />
            <div className="flex justify-end">
              <button
                onClick={() => setShowNameModal(false)}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded mr-2"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleAddConfig(launchConfigName, "testConfig");
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestConfigTab;
